import { Component, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SharedIdleService } from '@sbd-doug-frontend/shared/lib/shared-services/shared-idle.service';
import { map, Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IdleModalComponent } from '@sbd-doug-frontend/shared/lib/shared-components/idle-modal/idle-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '@sbd-account-portal/app/core/user/user.service';
import { AccountPortalAuthService } from '@sbd-account-portal/app/core/auth/auth.service';
import { CIPXDrawerManagerService } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';

@Component({
  selector: 'sbd-account-portal-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent {
  title = 'Surety Bonds Direct Account';
  _sharedIdleService = inject(SharedIdleService);
  _unsubscribeAll: Subject<void> = new Subject<void>();
  _router = inject(Router);
  _activatedRoute = inject(ActivatedRoute);
  _userService = inject(UserService);
  _authService = inject(AccountPortalAuthService);
  dialog = inject(MatDialog);
  _drawerService = inject(CIPXDrawerManagerService)

  idleService$ = toObservable(this._sharedIdleService.idleExceeded).pipe(
    takeUntil(this._unsubscribeAll),
    map((isIdle: boolean) => {
      if (isIdle) {
        this._authService.isTokenRefreshable().subscribe(isRefreshable => {
          if (isRefreshable && this._userService.currentAccountUser()?.id) {
            const dialogRef = this.dialog.open(IdleModalComponent, { disableClose: true });

            // Start the modal timeout when dialog opens
            this._sharedIdleService.startModalTimeout();

            dialogRef.afterClosed().subscribe(result => {
              // Reset modal timeout when dialog closes
              this._sharedIdleService.resetModalTimeout();
              if (result) {
                this._sharedIdleService.refreshToken().subscribe();
                this._sharedIdleService.idleExceeded.set(false);
                this._sharedIdleService.startIdleDetection();
              } else {
                this.logout();
              }
            });
          } else if (!isRefreshable && this._userService.currentAccountUser()?.id) {
            const dialogData = {
              title: 'Session Expired',
              content: 'Your session has expired. Please sign in again.',
              showNoButton: false,
              yesButtonText: 'Sign in',
            };
            const dialogRef = this.dialog.open(IdleModalComponent, { data: { ...dialogData }, disableClose: true });

            // Start the modal timeout when dialog opens
            this._sharedIdleService.startModalTimeout();

            dialogRef.afterClosed().subscribe(result => {
              if (result) {
                this.logout();
              }
            });
          }
        });
      }
    }),
  ).subscribe();


  showLogoutDialog$ = toObservable(this._authService.tokenNotRenewable).pipe(
    takeUntil(this._unsubscribeAll),
    map((tokenNotRenewable) => {
      if (tokenNotRenewable) {
        const dialogData = {
          title: 'Session Expired',
          content: 'Your session has expired. Please sign in again !',
          showNoButton: false,
          yesButtonText: 'Sign in',
        };
        const dialogRef = this.dialog.open(IdleModalComponent, { data: { ...dialogData }, disableClose: true });

        // Start the modal timeout when dialog opens
        this._sharedIdleService.startModalTimeout();

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.logout();
          }
        });
      }
    })
  ).subscribe();

  // New subscription to handle modal timeout
  modalTimeout$ = toObservable(this._sharedIdleService.modalTimeout$).pipe(
    takeUntil(this._unsubscribeAll),
    map((timeoutExceeded) => {
      if (timeoutExceeded) {
        // Close any open dialogs and redirect to login
        this.dialog.closeAll();
        this.logout();
        this._sharedIdleService.resetModalTimeout();
      }
    })
  ).subscribe();

  logout(){
    this._drawerService.closeAllDrawers()
    this._router.navigate(['/sign-out'], { relativeTo: this._activatedRoute });
  }

  constructor() {
    // Start idle detection immediately when the page loads
    effect(() => {
      if (!this._sharedIdleService.idleCheckerStarted() && this._userService.currentAccountUser()?.id) {
        this._sharedIdleService.idleCheckerStarted.set(true);
        this._sharedIdleService.startIdleDetection();
      }
    });
  }
}
